import { Link, useLocation } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Universities', path: '/universities' },
    { name: 'Online Courses', path: '/courses' },
    { name: 'Study in USA', path: '/study-usa' },
    { name: 'Study in Europe', path: '/study-europe' },
    { name: 'Scholarships', path: '/scholarships' },
    { name: 'Skills', path: '/skills' },
    { name: 'Rankings', path: '/rankings' },
    { name: 'Deals for Students', path: '/blog' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-sm'}`}>
      {/* Top utility bar */}
      <div className="bg-primary">
        <div className="container-custom">
          <div className="flex items-center justify-between h-9 text-xs">
            <div className="flex items-center gap-6">
              <Link to="/about" className="text-white/80 hover:text-[hsl(var(--gold))] transition-colors font-medium" data-testid="about-link">
                About Us
              </Link>
              <Link to="/contact" className="text-white/80 hover:text-[hsl(var(--gold))] transition-colors font-medium" data-testid="contact-link">
                Contact
              </Link>
              <Link to="/advertise" className="text-white/80 hover:text-[hsl(var(--gold))] transition-colors font-medium" data-testid="advertise-link">
                Advertise
              </Link>
            </div>
            <div className="hidden md:flex items-center">
              <span className="text-white/60 text-xs">Your Trusted Education Resource</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_campus-intel-1/artifacts/acmvr2ui_inside%20Higher%20Logo%20new.png" 
              alt="InsideHigherEducation" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden 2xl:flex items-center gap-0.5 mx-4" aria-label="Main navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-2 py-2 text-sm font-semibold transition-colors duration-200 whitespace-nowrap rounded-sm ${
                  isActive(item.path)
                    ? 'text-secondary border-b-2 border-[hsl(var(--gold))]'
                    : 'text-primary hover:text-secondary hover:bg-muted'
                }`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Search & Mobile Menu */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <form onSubmit={handleSearch} className="hidden md:flex items-center">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-44 pr-9 h-9 text-sm border-gray-200 focus:border-primary focus:ring-primary/20 rounded-sm"
                  data-testid="search-input"
                />
                <button
                  type="submit"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                  data-testid="search-button"
                >
                  <Search size={16} />
                </button>
              </div>
            </form>

            <Button
              variant="ghost"
              size="icon"
              className="2xl:hidden hover:bg-muted"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="2xl:hidden border-t bg-white shadow-lg max-h-[calc(100dvh-100px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-custom py-4">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 rounded-sm"
                  data-testid="mobile-search-input"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  data-testid="mobile-search-button"
                >
                  <Search size={18} />
                </button>
              </div>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-semibold py-3 px-2 rounded-sm transition-colors ${
                    isActive(item.path)
                      ? 'text-secondary bg-muted'
                      : 'text-primary hover:text-secondary hover:bg-muted'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.path.slice(1)}`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
